import { LoadingPanel } from "@/components/LoadingPanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/cn";
import { getSupabase } from "@/lib/supabase";
import { ArrowLeft, ImagePlus, Send, Video, X } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

type ChatAttachment = { path: string; mime: string; previewUrl: string };
type Message = {
  id: string;
  role: "user" | "assistant";
  content: string;
  attachments?: ChatAttachment[];
};

function newMessageId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

type ChatResponse = {
  session_id: string;
  reply: string;
  status: "open" | "resolved" | "escalated";
  redirect_url: string | null;
  error?: string;
};

const REDIRECT_COUNTDOWN_SECONDS = 5;
const ALLOWED_IMAGE_MIME = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
const MAX_IMAGE_BYTES = 8 * 1024 * 1024; // 8 MB

export function RepairChat() {
  const { loading: authLoading, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [redirect, setRedirect] = useState<{ url: string; remaining: number } | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const seededRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const greeting = useMemo<Message>(
    () => ({
      id: "greeting",
      role: "assistant",
      content:
        "Hi, I'm here to help with anything that's not working at your place. Tell me what's going on — even a short description is fine.",
    }),
    [],
  );

  // Initial greeting once auth is loaded.
  useEffect(() => {
    if (!authLoading && user && messages.length === 0) {
      setMessages([greeting]);
    }
  }, [authLoading, user, messages.length, greeting]);

  // Auto-send `?initial_message=...` as the first user message (e.g. when a
  // tenant arrives via a magic-link emailed by the cold-intake agent with their
  // original email body pre-seeded). Runs once after greeting is shown.
  const initialMessage = searchParams.get("initial_message");
  // biome-ignore lint/correctness/useExhaustiveDependencies: send + searchParams + setSearchParams identities churn; this effect must only fire once per (user, initialMessage) and is guarded by seededRef.
  useEffect(() => {
    if (authLoading || !user || seededRef.current || !initialMessage) return;
    if (messages.length === 0) return; // wait for greeting
    seededRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete("initial_message");
    setSearchParams(next, { replace: true });
    void send(initialMessage);
  }, [authLoading, user, messages.length, initialMessage]);

  // Auto-scroll on new messages. messages.length + sending are listed as
  // triggers (no closure read inside) so biome would otherwise flag them as
  // "unnecessary".
  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger-only deps for the scroll-to-bottom effect
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, sending]);

  // Redirect countdown.
  useEffect(() => {
    if (!redirect) return;
    if (redirect.remaining <= 0) {
      window.location.assign(redirect.url);
      return;
    }
    const t = setTimeout(
      () => setRedirect((r) => (r ? { ...r, remaining: r.remaining - 1 } : r)),
      1000,
    );
    return () => clearTimeout(t);
  }, [redirect]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      const atts = pendingAttachments;
      if (!trimmed && atts.length === 0) return;
      setSending(true);
      setGlobalError(null);
      setMessages((m) => [
        ...m,
        { id: newMessageId(), role: "user", content: trimmed, attachments: atts },
      ]);
      setPendingAttachments([]);
      try {
        const supabase = getSupabase();
        const { data, error } = await supabase.functions.invoke<ChatResponse>("tenant-chat", {
          body: {
            message: trimmed,
            session_id: sessionId,
            attachments: atts.map((a) => ({ path: a.path, mime: a.mime })),
          },
        });
        if (error) {
          setGlobalError(error.message ?? "Something went wrong.");
          return;
        }
        if (!data) {
          setGlobalError("Empty response.");
          return;
        }
        if (data.error && data.error !== "no_tenancy") {
          setGlobalError(data.error);
          return;
        }
        if (data.session_id) setSessionId(data.session_id);
        if (data.reply) {
          setMessages((m) => [
            ...m,
            { id: newMessageId(), role: "assistant", content: data.reply },
          ]);
        }
        if (data.redirect_url) {
          setRedirect({ url: data.redirect_url, remaining: REDIRECT_COUNTDOWN_SECONDS });
        }
      } catch (e) {
        setGlobalError(e instanceof Error ? e.message : String(e));
      } finally {
        setSending(false);
      }
    },
    [sessionId, pendingAttachments],
  );

  const onPickFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0 || !sessionId) {
        if (!sessionId) setGlobalError("Send a first message before attaching photos.");
        return;
      }
      setUploading(true);
      setGlobalError(null);
      try {
        const supabase = getSupabase();
        const next: ChatAttachment[] = [...pendingAttachments];
        for (const file of Array.from(files)) {
          if (!ALLOWED_IMAGE_MIME.includes(file.type)) {
            setGlobalError(`${file.name}: unsupported type. Use jpg / png / webp / heic.`);
            continue;
          }
          if (file.size > MAX_IMAGE_BYTES) {
            setGlobalError(`${file.name}: too big (max 8 MB).`);
            continue;
          }
          if (next.length >= 4) {
            setGlobalError("Up to 4 photos per message.");
            break;
          }
          const ext = (file.name.split(".").pop() ?? "jpg").toLowerCase();
          const path = `${sessionId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
          const { error: upErr } = await supabase.storage
            .from("chat-attachments")
            .upload(path, file, { contentType: file.type });
          if (upErr) {
            setGlobalError(`Upload failed: ${upErr.message}`);
            continue;
          }
          next.push({ path, mime: file.type, previewUrl: URL.createObjectURL(file) });
        }
        setPendingAttachments(next);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [sessionId, pendingAttachments],
  );

  const removeAttachment = useCallback(
    async (idx: number) => {
      const a = pendingAttachments[idx];
      if (!a) return;
      setPendingAttachments((p) => p.filter((_, i) => i !== idx));
      URL.revokeObjectURL(a.previewUrl);
      try {
        await getSupabase().storage.from("chat-attachments").remove([a.path]);
      } catch {
        // best-effort cleanup; orphaned objects are fine
      }
    },
    [pendingAttachments],
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (sending || redirect) return;
    const v = input;
    setInput("");
    void send(v);
  };

  if (authLoading) return <LoadingPanel />;

  return (
    <div className="flex flex-col gap-4 max-w-2xl mx-auto w-full">
      <div className="flex items-center gap-3">
        <Link to="/repairs" className="text-sm text-ink-dim hover:text-ink flex items-center gap-1">
          <ArrowLeft size={14} /> Repairs
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Need help?</h1>
          <p className="text-sm text-ink-dim mt-1">
            Chat to Trinium about anything broken. We can usually walk you through a fix or connect
            you to an engineer over video in a couple of taps.
          </p>
        </div>
      </div>

      <Card className="flex flex-col" style={{ minHeight: "60vh" }}>
        <div ref={scrollerRef} className="flex-1 p-4 md:p-6 flex flex-col gap-3 overflow-y-auto">
          {messages.map((m) => (
            <Bubble key={m.id} role={m.role} content={m.content} />
          ))}
          {sending ? <Typing /> : null}
          {redirect ? (
            <RedirectCard
              url={redirect.url}
              remaining={redirect.remaining}
              onCancel={() => setRedirect(null)}
            />
          ) : null}
        </div>
        {globalError ? (
          <p role="alert" className="text-xs text-red-300 px-4 md:px-6 pb-2">
            {globalError}
          </p>
        ) : null}
        <form onSubmit={onSubmit} className="border-t border-line p-3 md:p-4 flex flex-col gap-2">
          {pendingAttachments.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {pendingAttachments.map((a, i) => (
                <div
                  key={a.path}
                  className="relative w-16 h-16 rounded-lg overflow-hidden border border-line bg-bg-card"
                >
                  <img src={a.previewUrl} alt="attachment" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeAttachment(i)}
                    className="absolute top-0 right-0 p-0.5 bg-bg/80 text-ink rounded-bl-md"
                    aria-label="Remove attachment"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              multiple
              capture="environment"
              className="hidden"
              onChange={(e) => onPickFiles(e.target.files)}
              disabled={sending || uploading || !!redirect || !sessionId}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || uploading || !!redirect || !sessionId}
              title={sessionId ? "Add photo" : "Send a message first"}
              className="shrink-0 h-11 w-11 inline-flex items-center justify-center rounded-xl border border-line text-ink-dim hover:text-ink hover:bg-white/5 disabled:opacity-40"
              aria-label="Add photo"
            >
              <ImagePlus size={18} />
            </button>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Tell us what's going on…"
              rows={1}
              disabled={sending || !!redirect}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSubmit(e as unknown as FormEvent);
                }
              }}
              className="flex-1 resize-none bg-bg-card border border-line rounded-xl px-3.5 py-2.5 text-base md:text-[15px] text-ink placeholder:text-ink-dim/60 focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-1 focus-visible:ring-accent/40"
            />
            <Button
              type="submit"
              disabled={
                sending ||
                uploading ||
                !!redirect ||
                (!input.trim() && pendingAttachments.length === 0)
              }
            >
              <Send size={14} />
              Send
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function Bubble({ role, content, attachments }: Omit<Message, "id">) {
  const isUser = role === "user";
  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[80%] rounded-2xl px-3.5 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap flex flex-col gap-2",
          isUser
            ? "bg-accent text-accent-on rounded-br-md"
            : "bg-bg-card border border-line text-ink rounded-bl-md",
        )}
      >
        {attachments && attachments.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((a) => (
              <img
                key={a.path}
                src={a.previewUrl}
                alt="attachment"
                className="rounded-md max-h-40 max-w-[12rem] object-cover border border-black/10"
              />
            ))}
          </div>
        ) : null}
        {content ? <span>{content}</span> : null}
      </div>
    </div>
  );
}

function Typing() {
  return (
    <div className="flex justify-start">
      <div className="bg-bg-card border border-line text-ink-dim rounded-2xl rounded-bl-md px-3.5 py-3 text-sm">
        <span className="inline-block animate-pulse">Thinking</span>
        <span className="inline-block ml-1 animate-pulse">…</span>
      </div>
    </div>
  );
}

function RedirectCard({
  url,
  remaining,
  onCancel,
}: {
  url: string;
  remaining: number;
  onCancel: () => void;
}) {
  return (
    <Card className="border-accent/30 bg-accent/5 mt-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Video size={18} className="text-accent" />
          Connecting you to a Trinium engineer
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-ink-dim">
          Opening the video session in {remaining} second{remaining === 1 ? "" : "s"}…
        </p>
        <div className="flex gap-2">
          <Button onClick={() => window.location.assign(url)}>
            <Video size={14} /> Open now
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Stay in chat
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
